import type { CollectionReference, DocumentData } from 'firebase/firestore'
import {
  collection,
  doc,
  limit,
  orderBy,
  query,
  runTransaction,
  serverTimestamp,
  type Firestore,
  type FirestoreDataConverter,
  type QueryDocumentSnapshot,
} from 'firebase/firestore'
import {
  collectionSnapshots,
  convertDocumentSnapshotToModel,
} from '../../firestore-mobx/stream'
import type { FirebaseRepository } from '../../models/FirebaseRepository'
import { buildUnsupportedConverter } from '../shared/noops'
import type { FirestoreCurrentPromptVersion } from './schema'
import { schema } from './schema'
import { PromptType, PromptVersionStatus } from '../PromptVersion/types'
import type { InteractiveElementPromptType } from '../PromptInteractiveElementType/types'
import type { RubricType } from '../PromptRubricType'
import type { PostRoomStatePromptType } from '../PromptPostRoomStateType/types'
import { CurrentPromptVersion } from '../../models/CurrentPromptVersion'
import {
  getPromptVersionConverter,
  getPromptVersionDocRef,
} from '../PromptVersion'
import { type RoomStatePromptType } from '../PromptRoomStateType/types'
import { type ToolPromptType } from '../PromptToolType/types'
import { type PostAssignmentPromptType } from '../PromptPostAssignmentType/types'

export * from './schema'

const unsupported = buildUnsupportedConverter<FirestoreCurrentPromptVersion>()

const readConverter: FirestoreDataConverter<FirestoreCurrentPromptVersion> = {
  toFirestore: unsupported.toFirestore,
  fromFirestore: (snapshot: QueryDocumentSnapshot) => {
    const data = snapshot.data({ serverTimestamps: 'estimate' })
    return schema.parse(data)
  },
}

const getReadCollectionRef = (
  firestore: Firestore,
  {
    promptType,
    subType,
  }: {
    promptType: PromptType
    subType:
      | RubricType
      | InteractiveElementPromptType
      | PostRoomStatePromptType
      | RoomStatePromptType
      | ToolPromptType
      | PostAssignmentPromptType
  }
): CollectionReference<FirestoreCurrentPromptVersion, DocumentData> => {
  return collection(
    firestore,
    'prompt',
    promptType,
    'type',
    subType.toString(),
    'current_prompt_version'
  ).withConverter(readConverter)
}

export const getCurrentPromptVersion = (
  repository: FirebaseRepository,
  params: Parameters<typeof getReadCollectionRef>[1]
) => {
  const col = getReadCollectionRef(repository.firestore, params)
  const q = query(col, orderBy('updatedAt', 'desc'), limit(1))
  const result = collectionSnapshots(q, { wrapped: true }).map((snapshot) => {
    const models = snapshot.docs.map((doc) => {
      return convertDocumentSnapshotToModel(
        repository,
        doc,
        CurrentPromptVersion
      )
    })
    if (!models.length) return null
    return models[0]
  })
  return result
}

export const getCurrentPromptVersions = (
  repository: FirebaseRepository,
  params: Parameters<typeof getReadCollectionRef>[1]
) => {
  const col = getReadCollectionRef(repository.firestore, params)
  const q = query(col, orderBy('updatedAt', 'desc'))
  return collectionSnapshots(q, { wrapped: true }).map((snapshot) => {
    return snapshot.docs.map((doc) => {
      return convertDocumentSnapshotToModel(
        repository,
        doc,
        CurrentPromptVersion
      )
    })
  })
}

export const installNewPromptVersion = async (
  repository: FirebaseRepository,
  {
    promptType,
    subType,
    installNotes,
    previousPromptVersionId,
    promptVersionId,
  }: {
    promptType: PromptType
    subType:
      | RubricType
      | InteractiveElementPromptType
      | PostRoomStatePromptType
      | RoomStatePromptType
      | ToolPromptType
      | PostAssignmentPromptType
    installNotes: string
    previousPromptVersionId: string
    promptVersionId: string
  }
) => {
  const colRef = collection(
    repository.firestore,
    'prompt',
    promptType,
    'type',
    subType.toString(),
    'current_prompt_version'
  )

  await runTransaction(repository.firestore, async (tx) => {
    const [previousVersion, newVersion] = await Promise.all(
      [previousPromptVersionId, promptVersionId].map((versionId) => {
        switch (promptType) {
          case PromptType.rubric: {
            const rubricType = subType as RubricType
            const docRef = getPromptVersionDocRef(repository, {
              promptType,
              subType: rubricType,
              versionId,
            }).withConverter(getPromptVersionConverter(promptType))
            return tx.get(docRef)
          }
          case PromptType.interactiveElement: {
            const interactiveElementType =
              subType as InteractiveElementPromptType
            const docRef = getPromptVersionDocRef(repository, {
              promptType,
              subType: interactiveElementType,
              versionId,
            }).withConverter(getPromptVersionConverter(promptType))
            return tx.get(docRef)
          }
          case PromptType.postRoomState: {
            const postRoomStateType = subType as PostRoomStatePromptType
            const docRef = getPromptVersionDocRef(repository, {
              promptType,
              subType: postRoomStateType,
              versionId,
            }).withConverter(getPromptVersionConverter(promptType))
            return tx.get(docRef)
          }
          case PromptType.roomState: {
            const roomStateType = subType as RoomStatePromptType
            const docRef = getPromptVersionDocRef(repository, {
              promptType,
              subType: roomStateType,
              versionId,
            }).withConverter(getPromptVersionConverter(promptType))
            return tx.get(docRef)
          }
          case PromptType.tool: {
            const toolType = subType as ToolPromptType
            const docRef = getPromptVersionDocRef(repository, {
              promptType,
              subType: toolType,
              versionId,
            }).withConverter(getPromptVersionConverter(promptType))
            return tx.get(docRef)
          }
          case PromptType.postAssignment: {
            const postAssignmentType = subType as PostAssignmentPromptType
            const docRef = getPromptVersionDocRef(repository, {
              promptType,
              subType: postAssignmentType,
              versionId,
            }).withConverter(getPromptVersionConverter(promptType))
            return tx.get(docRef)
          }
        }
      })
    )

    if (!previousVersion.exists())
      throw new Error(
        `Previous version ${previousPromptVersionId} does not exist`
      )

    const newVersionData = newVersion.data()

    if (!newVersion.exists() || !newVersionData)
      throw new Error(`New version ${promptVersionId} does not exist`)

    if (newVersionData.versionStatus !== PromptVersionStatus.published)
      throw new Error(
        `New version to install ${promptVersionId} is not published`
      )

    const newCurrentVersionDocRef = doc(colRef.withConverter(readConverter))

    return tx.set(newCurrentVersionDocRef.withConverter(null), {
      installNotes,
      previousPromptVersionId,
      promptVersionId,
      updatedAt: serverTimestamp(),
      userId: repository.uid,
    })
  })
}
